import React, { useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Alert from "@/components/Base/Alert";
import Lucide from "@/components/Base/Lucide";

export default function SupervisorLogin() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const savedEmail = localStorage.getItem("heineken_remembered_email");
    const savedPassword = localStorage.getItem("heineken_remembered_password");

    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
    if (savedPassword) setPassword(savedPassword);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Please fill in all fields");
      return;
    }

    setLoading(true);
    setError("");

    try {
      if (rememberMe) {
        localStorage.setItem("heineken_remembered_email", email);
        localStorage.setItem("heineken_remembered_password", password);
      } else {
        localStorage.removeItem("heineken_remembered_email");
        localStorage.removeItem("heineken_remembered_password");
      }

      const envApiUrl = import.meta.env.VITE_API_URL as string | undefined;
      // If the env points to localhost, override with the current host (needed for mobile browsers)
      const apiUrl = (() => {
        const fallback = `${window.location.protocol}//${window.location.hostname}:4000`;
        if (!envApiUrl) return fallback;
        try {
          const u = new URL(envApiUrl);
          if (u.hostname === "localhost" || u.hostname === "127.0.0.1") return fallback;
          return envApiUrl;
        } catch {
          return envApiUrl || fallback;
        }
      })();
      const response = await fetch(`${apiUrl}/auth/login-web`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          usernameOrEmail: email.trim(),
          password,
        }),
      });

      const data: any = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.message || data.error || "Login failed");
        return;
      }

      localStorage.setItem("token", data.token);
      if (data?.user?.role) localStorage.setItem("role", data.user.role);
      if (typeof data?.user?.firstName === "string") {
        localStorage.setItem("userFirstName", data.user.firstName);
      }
      if (typeof data?.user?.lastName === "string") {
        localStorage.setItem("userLastName", data.user.lastName);
      }
      if (typeof data?.user?.teamName === "string") {
        localStorage.setItem("userTeamName", data.user.teamName);
      } else {
        localStorage.removeItem("userTeamName");
      }
      navigate("/app", { replace: true });
    } catch (err) {
      console.error("Login error:", err);
      setError("Unable to connect to server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col max-w-md mx-auto w-full">
      <div className="flex-1 overflow-y-auto p-4 sm:p-8 bg-gray-50">
        <div className="max-w-full w-full mx-auto px-4 pb-6 bg-white rounded-lg shadow-sm border border-gray-200 mt-8">
          <div className="pt-6 pb-3">
            <div className="rounded-[0.8rem] w-[55px] h-[55px] border border-primary/30 flex items-center justify-center mx-auto">
              <div className="relative flex items-center justify-center w-[50px] rounded-[0.6rem] h-[50px] bg-gradient-to-b from-theme-1/90 to-theme-2/90 bg-white">
                <div className="w-[26px] h-[26px] relative -rotate-45 [&_div]:bg-white">
                  <div className="absolute w-[20%] left-0 inset-y-0 my-auto rounded-full opacity-50 h-[75%]" />
                  <div className="absolute w-[20%] inset-0 m-auto h-[120%] rounded-full" />
                  <div className="absolute w-[20%] right-0 inset-y-0 my-auto rounded-full opacity-50 h-[75%]" />
                </div>
              </div>
            </div>

            <div className="mt-10">
              <div className="text-2xl font-medium text-center">ConnectDots StockLedger</div>
              <div className="mt-2.5 text-slate-600 dark:text-slate-400 text-center">
                Supervisor/Team Leader Login
              </div>

              <Alert
                variant="outline-primary"
                className="flex items-center px-4 py-3 my-7 bg-primary/5 border-primary/20 rounded-[0.6rem] leading-[1.7]"
              >
                {({ dismiss }) => (
                  <>
                    <div className="">
                      <Lucide
                        icon="Lightbulb"
                        className="stroke-[0.8] w-7 h-7 mr-2 fill-primary/10"
                      />
                    </div>
                    <div className="ml-1 mr-8 text-slate-700 dark:text-slate-300">
                      Welcome to <span className="font-medium">StockLedger</span>. Enter your credentials and click{" "}
                      <span className="font-medium">Login</span> to continue.
                    </div>
                    <Alert.DismissButton
                      type="button"
                      className="btn-close text-primary"
                      onClick={dismiss}
                      aria-label="Close"
                    >
                      <Lucide icon="X" className="w-5 h-5" />
                    </Alert.DismissButton>
                  </>
                )}
              </Alert>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <label className="inline-block mb-2">Username*</label>
            <input
              type="text"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your assign username"
              className="transition duration-200 ease-in-out w-full text-sm shadow-sm placeholder:text-slate-400/90 focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus:border-primary focus:border-opacity-40 block rounded-[0.6rem] border-slate-300/80 px-4 py-3.5"
            />

            <label className="inline-block mb-2 mt-4">Password*</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="************"
                className="transition duration-200 ease-in-out w-full text-sm shadow-sm placeholder:text-slate-400/90 focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus:border-primary focus:border-opacity-40 block rounded-[0.6rem] border-slate-300/80 px-4 py-3.5 pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 w-11 flex items-center justify-center text-slate-400 hover:text-slate-600"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>

            <div className="flex mt-4 text-xs text-slate-500 sm:text-sm">
              <div className="flex items-center mr-auto">
                <input
                  type="checkbox"
                  id="remember-me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="transition-all duration-100 ease-in-out shadow-sm border-slate-200 cursor-pointer rounded focus:ring-4 focus:ring-offset-0 focus:ring-primary focus:ring-opacity-20 mr-2.5 border"
                />
                <label className="cursor-pointer select-none" htmlFor="remember-me">
                  Remember me
                </label>
              </div>
            </div>

            {error ? (
              <div className="mt-4 rounded-md px-4 py-3 bg-red-50 text-red-600 border border-red-200">
                {error}
              </div>
            ) : null}

            <div className="mt-5 text-center xl:mt-8 xl:text-left">
              <button
                type="submit"
                disabled={loading}
                className="transition duration-200 border shadow-sm inline-flex items-center justify-center px-3 font-medium cursor-pointer focus:ring-4 focus:ring-primary focus:ring-opacity-20 rounded-full w-full bg-primary border-primary text-white py-3.5 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? "Logging in..." : "LOGIN"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

